/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import { Dropdown, Text, Panel, PanelType, Stack } from "@fluentui/react";
import { HuxTextBox } from "@nuance/hux-components";
import { IEnvPanelProps } from "../../EnvironmentSettings/EnvironmentViewModels";
import { EnvironmentPanelCommands, IEnvPanelState } from "./EnvironmentPanelViewModel";
import { SettingContent } from "@nuance/hap-components";
import {
    IFieldUI,
    ISettingValueEx,
    IUIControl,
    SettingLevel
} from "@nuance/hierarchical-settings-ui-domain";
import { IPanelFooterProps } from "../../../MainView/Panels/IPanelFooterProps";
import PanelFooter from "../../../MainView/Panels/PanelFooter";

const stackTokens = { childrenGap: 10 };

const envPanelCommands = new EnvironmentPanelCommands();
const cmds = envPanelCommands.Cmds;

export const EnvironmentPanel = (props: IEnvPanelProps): JSX.Element => {
    const initialState: IEnvPanelState = {
        definition: props.definition,
        errors: {},
        touched: {},
        isValid: !props.data.isNew,
        isDirty: false,
        data: { ...props.data }
    };

    const [state, dispatch] = React.useReducer(envPanelCommands.Reducer, initialState);

    const onSave = () => {
        if (props.onSaveContent != undefined) {
            props.onSaveContent(state.data);
        }
    };

    const footerProps: IPanelFooterProps = {
        onSave,
        onCancel: props.onClosePanel,
        isNew: state.data.isNew,
        isDirty: state.isDirty,
        isValid: state.isValid
    };

    const fieldUI: IUIControl | undefined = props.definition.UI.Controls.find(c => {
        return (c as IFieldUI).SettingName === state.data.settingName;
    });

    const settingValue: ISettingValueEx = {
        SettingName: state.data.settingName,
        Value: state.data.defaultValue,
        Locked: false,
        ValueSource: {
            Level: SettingLevel.Organization,
            UID: 0,
            Name: "(Setting default)",
            LockApplied: false
        }
    };

    return (
        <Panel
            isOpen={props.isPanelOpen}
            onDismiss={props.onClosePanel}
            headerText={props.panelTitle}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={() => {
                return PanelFooter(footerProps);
            }}
            isFooterAtBottom={true}
            type={PanelType.medium}
        >
            <Stack tokens={stackTokens}>
                <Dropdown
                    label="Setting Name"
                    required
                    disabled={!state.data.isNew}
                    selectedKey={state.data.settingName}
                    options={props.definition.Fields?.map(item => ({
                        key: item.SettingName,
                        text: item.SettingName
                    }))}
                    onChange={(_, selected) =>
                        dispatch(
                            cmds.onSettingNameChange(
                                selected !== undefined ? selected.key.toString() : ""
                            )
                        )
                    }
                />

                <HuxTextBox
                    label="Environment"
                    name="environment"
                    required
                    onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                    errorMessage={state.touched.environment ? state.errors.environment : ""}
                    onChange={(e, value) => {
                        dispatch(
                            cmds.onTextChanged({
                                name: e.currentTarget.name,
                                value: value ?? ""
                            })
                        );
                    }}
                    value={state.data.environment}
                />

                <Text>Specify the default value for this environment:</Text>

                {fieldUI && (
                    <SettingContent
                        fieldUI={fieldUI}
                        settingValue={settingValue}
                        settingDefinition={props.definition}
                        onValueChange={(e, value) => {
                            dispatch(cmds.onTextChanged({ name: "defaultValue", value }));
                        }}
                        language="en-us"
                        currentLevel={SettingLevel.Organization}
                        isReadOnly={false}
                    />
                )}
            </Stack>
        </Panel>
    );
};
